import { NonIndexRouteObject, RouteObject } from 'react-router-dom';

import { RootLayout, UnauthenticatedLayout } from '@/components';
import { TechRelationshipsAdminPage } from '@/pages/admin/tech-relationships';
import { AnimalManagementPage } from '@/pages/animal-management';
import { AspirationPage } from '@/pages/aspiration';
import { AuditLogPage } from '@/pages/audit';
import { AuditHomePage } from '@/pages/audit/home/AuditHomePage';
import { AuthCallbackPage } from '@/pages/auth-callback';
import { BulkDonorPage } from '@/pages/bulk-donor';
import { BulkDonorSchedulePage } from '@/pages/bulk-donor-schedule';
import { AgllcBulkDonorSchedulePage } from '@/pages/bulk-donor-schedule/agllc';
import { FarbroBulkDonorSchedulePage } from '@/pages/bulk-donor-schedule/farbro';
import { BulkDonorSchedulingPage } from '@/pages/bulk-donor-scheduling';
import { CalfEntryPage } from '@/pages/calf-entry';
import { ChuteSchedulerRoute } from '@/pages/chute-scheduler/ChuteSchedulerRoute';
import { CreateShipmentPage } from '@/pages/create-shipment';
import { CsrDay7Page } from '@/pages/day-7/csr-day-7';
import { RecipSummaryPage } from '@/pages/day-7/csr-day-7/RecipSummary';
import { SummaryPage } from '@/pages/day-7/csr-day-7/Summary';
import { PseDay7FrozenEmbryosPage, PseDay7Page } from '@/pages/day-7/pse-day-7';
import { DonorSchedulingPartnershipPage } from '@/pages/donor-scheduling-partnership';
import { EmbryoAppPage } from '@/pages/embryo-app';
import { TabContent } from '@/pages/embryo-app/components/TabContent';
import { tabConfig } from '@/pages/embryo-app/util/constants';
import { EmbryoDetailsPage } from '@/pages/embryo-details';
import { FertWorkPage } from '@/pages/fert-work';
import { FertWorkHistory } from '@/pages/fert-work/components/FertWorkHistory';
import { FertWorkSession } from '@/pages/fert-work/components/FertWorkSession';
import { GenerateSOFLabels } from '@/pages/fert-work/components/GenerateSOFLabels';
import { FertilizationPage } from '@/pages/fertilization';
import { FreezeV2Page } from '@/pages/freeze-v2';
import { FreezeRoute } from '@/pages/freeze-v2/FreezeRoute';
import { FreshShipmentPage } from '@/pages/fresh-shipment';
import { FreshStrawPage } from '@/pages/fresh-straw';
import { HomePage } from '@/pages/home';
import { InClinicPage } from '@/pages/in-clinic';
import { InClinicDataEntry } from '@/pages/in-clinic/components/DataEntry';
import { InClinicPathGate } from '@/pages/in-clinic/inClinicPath';
import {
  AllClosedRequests,
  AllOpenRequests,
  AllRequests,
  IssueSettingsPage,
  MyClosedRequests,
  MyOpenRequests,
  MyRequests,
  NewIssuePage,
  RequestDetail,
} from '@/pages/issues';
import { LegacyAppsPage } from '@/pages/legacy-apps';
import {
  OnFarmImplantEntryPage,
  OnFarmImplantSessionsPage,
} from '@/pages/on-farm-implant';
import { OpuSearchPage } from '@/pages/opu-search';
import SemenPage from '@/pages/semen-app';
import { BatchDetailTab } from '@/pages/semen-app/components/BatchDetailTab';
import { HomeTab } from '@/pages/semen-app/components/HomeTab';
import { ShippingIntegrationTab } from '@/pages/semen-app/components/ShippingIntegrationTab';
import { SemenPullPage } from '@/pages/semen-pull';
import { ServiceScheduler } from '@/pages/service-scheduler';
import { ShippingPage } from '@/pages/shipping';
import { ShipmentPage } from '@/pages/shipping-details';
import { StrippingPage } from '@/pages/stripping';
import { PermissionProtectedRoute, ProtectedRoutes } from '@/routes';
import { MODULES } from '@/services/auth';

type NonIndexRouteObjectWithOutlet = NonIndexRouteObject & {
  outlet?: string;
};

type RouteObjectWithOutlet = RouteObject & {
  children?: NonIndexRouteObjectWithOutlet[];
};

const inClinicRoutes: RouteObject[] = [
  {
    element: (
      <PermissionProtectedRoute permission={`${MODULES.CHUTE_IMPLANT_READ}`} />
    ),
    children: [
      {
        index: true,
        element: <InClinicPage />,
      },
      {
        path: 'data-entry',
        element: <InClinicDataEntry />,
      },
    ],
  },
];

export const routesConfig: RouteObjectWithOutlet[] = [
  {
    path: '/auth-callback',
    element: <AuthCallbackPage />,
  },
  {
    path: '/',
    element: <UnauthenticatedLayout />,
    children: [
      {
        element: <ProtectedRoutes />,
        children: [
          {
            element: <RootLayout />,
            children: [
              {
                index: true,
                element: <HomePage />,
              },
              {
                path: 'animal-management',
                element: (
                  <PermissionProtectedRoute
                    permission={`${MODULES.ANIMAL_MANAGEMENT}.READ`}
                  />
                ),
                children: [
                  {
                    index: true,
                    element: <AnimalManagementPage />,
                  },
                ],
              },
              {
                path: 'chute-implant',
                element: <InClinicPathGate />,
                children: inClinicRoutes,
              },
              {
                path: 'in-clinic',
                element: <InClinicPathGate />,
                children: inClinicRoutes,
              },
              {
                path: 'chute-scheduler',
                element: (
                  <PermissionProtectedRoute
                    permission={`${MODULES.CHUTE_SCHEDULER}.READ`}
                  />
                ),
                children: [
                  {
                    index: true,
                    element: <ChuteSchedulerRoute />,
                  },
                ],
              },
              {
                path: 'donor-scheduling',
                element: (
                  <PermissionProtectedRoute
                    permission={`${MODULES.DONOR_SCHEDULING_PARTNERSHIP}.READ`}
                  />
                ),
                children: [
                  {
                    index: true,
                    element: <DonorSchedulingPartnershipPage />,
                  },
                ],
              },
              {
                path: 'bulk/donor',
                element: (
                  <PermissionProtectedRoute
                    permission={`${MODULES.BULK_DONOR_SCHEDULING}.READ`}
                  />
                ),
                children: [
                  {
                    index: true,
                    element: <BulkDonorPage />,
                  },
                ],
              },
              {
                path: 'bulk/donor-schedule',
                element: (
                  <PermissionProtectedRoute
                    permission={`${MODULES.BULK_DONOR_SCHEDULING}.READ`}
                  />
                ),
                children: [
                  {
                    index: true,
                    element: <BulkDonorSchedulePage />,
                  },
                  {
                    path: 'farbro',
                    element: <FarbroBulkDonorSchedulePage />,
                  },
                  {
                    path: 'agllc',
                    element: <AgllcBulkDonorSchedulePage />,
                  },
                ],
              },
              {
                path: 'bulk-donor-scheduling',
                element: (
                  <PermissionProtectedRoute
                    permission={`${MODULES.BULK_DONOR_SCHEDULING}.READ`}
                  />
                ),
                children: [
                  {
                    index: true,
                    element: <BulkDonorSchedulingPage />,
                  },
                ],
              },
              {
                path: 'fertilization',
                element: (
                  <PermissionProtectedRoute
                    permission={`${MODULES.FERTILIZATION}.READ`}
                  />
                ),
                children: [
                  {
                    index: true,
                    element: <FertilizationPage />,
                  },
                ],
              },
              {
                path: 'fert-work',
                element: (
                  <PermissionProtectedRoute
                    permission={MODULES.FERT_WORK_READ}
                  />
                ),
                children: [
                  {
                    index: true,
                    element: <FertWorkPage />,
                  },
                  {
                    path: 'session',
                    element: <FertWorkSession />,
                  },
                  {
                    path: 'history',
                    element: <FertWorkHistory />,
                  },
                  {
                    path: 'sof-labels',
                    element: <GenerateSOFLabels />,
                  },
                ],
              },
              {
                path: 'on-farm-implant',
                handle: { presence: true },
                children: [
                  {
                    index: true,
                    element: <OnFarmImplantSessionsPage />,
                  },
                  {
                    path: 'sessions',
                    element: <OnFarmImplantSessionsPage />,
                  },
                  {
                    path: 'entry',
                    element: <OnFarmImplantEntryPage />,
                  },
                ],
              },
              {
                path: 'stripping',
                element: (
                  <PermissionProtectedRoute
                    permission={`${MODULES.STRIPPING}.READ`}
                  />
                ),
                children: [
                  {
                    index: true,
                    element: <StrippingPage />,
                  },
                ],
              },
              {
                path: 'opu-search',
                element: (
                  <PermissionProtectedRoute
                    permission={`${MODULES.OPU_SEARCH}.READ`}
                  />
                ),
                children: [
                  {
                    index: true,
                    element: <OpuSearchPage />,
                  },
                ],
              },

              {
                path: 'aspiration',
                element: (
                  <PermissionProtectedRoute
                    permission={`${MODULES.ASPIRATION}.READ`}
                  />
                ),
                children: [
                  {
                    index: true,
                    element: <AspirationPage />,
                  },
                ],
              },
              {
                path: 'semen-pull',
                element: (
                  <PermissionProtectedRoute
                    permission={`${MODULES.SEMEN_PULL}.READ`}
                  />
                ),
                children: [
                  {
                    index: true,
                    element: <SemenPullPage />,
                  },
                ],
              },
              {
                path: 'semen',
                element: <SemenPage />,
                children: [
                  {
                    index: true,
                    element: <HomeTab />,
                  },
                  {
                    path: 'detail',
                    element: <BatchDetailTab />,
                  },
                  {
                    path: 'shipping-integration',
                    element: <ShippingIntegrationTab />,
                  },
                  {
                    path: 'semen-pull',
                    element: <SemenPullPage />,
                  },
                ],
              },

              {
                path: 'fresh-shipment',
                element: (
                  <PermissionProtectedRoute
                    permission={`${MODULES.FRESH_SHIPMENT}.READ`}
                  />
                ),
                children: [
                  {
                    index: true,
                    element: <FreshShipmentPage />,
                  },
                  {
                    path: ':id',
                    element: <FreshShipmentPage />,
                  },
                ],
              },
              {
                path: 'freeze',
                element: (
                  <PermissionProtectedRoute
                    permission={`${MODULES.FREEZE}.READ`}
                  />
                ),
                children: [
                  {
                    index: true,
                    element: <FreezeRoute />,
                  },
                ],
              },
              {
                // Direct entrance to the rebuilt freeze app, bypassing the
                // freeze_app_v2 flag — for local dev and QA where flags may
                // be unavailable. Remove when the flag is retired.
                path: 'freeze-v2',
                element: (
                  <PermissionProtectedRoute
                    permission={`${MODULES.FREEZE}.READ`}
                  />
                ),
                children: [
                  {
                    index: true,
                    element: <FreezeV2Page />,
                  },
                ],
              },
              {
                path: 'embryo',
                element: <EmbryoAppPage />,
                children: tabConfig.map((tab, index) => ({
                  ...(index === 0 ? { index: true } : { path: tab.route }),
                  element: (
                    <TabContent
                      columnDefs={tab.columnDefs}
                      storageKey={tab.storageKey}
                      gridClassName={tab.gridClassName}
                      createDataSource={tab.createDataSource}
                    />
                  ),
                })),
              },
              {
                path: 'embryo/embryo',
                element: <EmbryoDetailsPage />,
              },
              {
                path: 'embryo/outside-embryo',
                element: <EmbryoDetailsPage />,
              },
              {
                path: 'admin/tech-relationships',
                element: <TechRelationshipsAdminPage />,
              },
              {
                path: 'fresh-straw-transfer',
                element: (
                  <PermissionProtectedRoute
                    permissions={[
                      `${MODULES.FRESH_STRAW}.READ`,
                      MODULES.OF_TRANSFER_READ,
                    ]}
                  />
                ),
                children: [
                  {
                    index: true,
                    element: <FreshStrawPage />,
                  },
                ],
              },
              {
                path: 'shipping',
                element: (
                  <PermissionProtectedRoute
                    permission={`${MODULES.SHIPPING}.READ`}
                  />
                ),
                children: [
                  {
                    index: true,
                    element: <ShippingPage />,
                  },
                ],
              },
              {
                path: 'create-shipment',
                element: <CreateShipmentPage />,
              },
              {
                path: 'create-shipment',
                element: <CreateShipmentPage />,
              },
              {
                path: 'day-7-transfer-csr',
                element: (
                  <PermissionProtectedRoute
                    permission={`${MODULES.DAY7}.READ`}
                  />
                ),
                children: [
                  {
                    index: true,
                    element: <CsrDay7Page />,
                  },
                ],
              },
              {
                path: 'day-7-transfer-csr/recip-summary',
                element: <RecipSummaryPage />,
              },
              {
                path: 'day-7-transfer-csr/summary',
                element: <SummaryPage />,
              },
              {
                path: 'day-7-transfer-pse',
                element: <PseDay7Page />,
              },
              {
                path: 'day-7-transfer-pse/frozen-embryos',
                element: <PseDay7FrozenEmbryosPage />,
              },
              {
                path: '/shipping/shipment',
                element: <ShipmentPage />,
              },
              {
                path: 'calf-entry',
                element: (
                  <PermissionProtectedRoute
                    permission={`${MODULES.CALF_ENTRY}.READ`}
                  />
                ),
                children: [
                  {
                    index: true,
                    element: <CalfEntryPage />,
                  },
                ],
              },
              {
                path: 'issues',
                element: (
                  <PermissionProtectedRoute
                    permission={`${MODULES.ISSUES}.READ`}
                  />
                ),
                children: [
                  {
                    path: 'my-issues',
                    element: (
                      <>
                        <MyRequests />
                      </>
                    ),
                  },
                  {
                    path: 'my-issues/open',
                    element: (
                      <>
                        <MyOpenRequests />
                      </>
                    ),
                  },
                  {
                    path: 'my-issues/closed',
                    element: (
                      <>
                        <MyClosedRequests />
                      </>
                    ),
                  },
                  {
                    path: 'all',
                    element: (
                      <>
                        <AllRequests />
                      </>
                    ),
                  },
                  {
                    path: 'all/open',
                    element: (
                      <>
                        <AllOpenRequests />
                      </>
                    ),
                  },
                  {
                    path: 'all/closed',
                    element: (
                      <>
                        <AllClosedRequests />
                      </>
                    ),
                  },
                  {
                    path: 'my-issues/detail',
                    element: (
                      <>
                        <RequestDetail />
                      </>
                    ),
                  },
                  {
                    path: 'new',
                    element: (
                      <>
                        <NewIssuePage />
                      </>
                    ),
                  },
                  {
                    path: 'settings',
                    element: (
                      <>
                        <IssueSettingsPage />
                      </>
                    ),
                  },
                ],
              },
              {
                path: 'legacy-apps',
                element: (
                  <PermissionProtectedRoute permission={MODULES.LEGACY_APPS} />
                ),
                children: [
                  {
                    index: true,
                    element: <LegacyAppsPage />,
                  },
                ],
              },
              {
                path: 'service-scheduler',
                element: (
                  <PermissionProtectedRoute
                    permission={`${MODULES.SERVICE_SCHEDULER}.READ`}
                  />
                ),
                children: [
                  {
                    index: true,
                    element: <ServiceScheduler />,
                  },
                ],
              },
              {
                path: 'audit',
                children: [
                  {
                    // Cross-company feed: admins only until events carry a company id.
                    element: (
                      <PermissionProtectedRoute permission={MODULES.DATABASE} />
                    ),
                    children: [{ index: true, element: <AuditHomePage /> }],
                  },
                  {
                    path: ':id',
                    element: <AuditLogPage />,
                  },
                ],
              },
              {
                path: '*',
                element: <div>Not Found</div>,
              },
            ],
          },
        ],
      },
    ],
  },
];
